import React, { useMemo } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import { Messages, TextField } from "./fields";

export const NOT_IN_LIST_TEXT = "not an OMS product group";
export const TAKEN_TEXT = "used by another override";
export const NO_GROUPS_TEXT = "No OMS product groups for this market, so an override cannot apply.";
export const LOAD_FAILED_TEXT =
  "The list of OMS product groups could not be loaded, so this name is not checked here. Type it exactly as it appears in the OMS.";

// Same look as the other warranty pickers. The menu is fixed-position so the scrolling editor
// does not clip it, and it is not portalled (style.css pins the portal under modals).
const selectStyles = {
  option: (provided) => ({ ...provided, fontSize: "13px" }),
  control: (provided, state) => ({
    ...provided,
    fontSize: "13px",
    borderColor: state.selectProps["aria-invalid"] ? "#F84646" : provided.borderColor,
  }),
};

// The product group of one override. The quote matches this name exactly against the OMS product
// group name, so it is picked from the OMS list and never typed freely. `groups` is
// { status: "loading" | "ready" | "error", names: string[] }. When the list is not available
// (could not be loaded, or the market has none) the field falls back to free text so staff are
// never blocked, with a line saying why. `takenNames` are the groups the other overrides of the
// same draft already use.
const ProductGroupField = ({ id, label, value, onChange, messages, disabled, groups, takenNames }) => {
  const names = groups.names;
  const taken = useMemo(() => new Set(takenNames), [takenNames]);
  const options = useMemo(
    () => names.map((name) => ({ value: name, label: name, taken: taken.has(name) })),
    [names, taken]
  );

  if (groups.status === "error") {
    return (
      <TextField
        id={id}
        label={label}
        value={value}
        maxLength={80}
        disabled={disabled}
        messages={messages}
        help={LOAD_FAILED_TEXT}
        onChange={onChange}
      />
    );
  }
  if (groups.status === "ready" && names.length === 0) {
    return (
      <TextField
        id={id}
        label={label}
        value={value}
        maxLength={80}
        disabled={disabled}
        messages={messages}
        help={NO_GROUPS_TEXT}
        onChange={onChange}
      />
    );
  }

  const ready = groups.status === "ready";
  const known = value !== "" && names.includes(value);
  let selected = null;
  if (known) {
    selected = options.find((option) => option.value === value);
  } else if (value !== "") {
    // Old data or a typo: shown as it is and still editable, marked once the list is known.
    selected = { value, label: value, unknown: ready };
  }
  const hasError = (messages || []).some((item) => item.severity === "error");
  const hasMessages = messages && messages.length > 0;

  return (
    <Form.Group className="mb-3">
      <Form.Label htmlFor={id}>{label}</Form.Label>
      <div className="selectMultiselect">
        <Select
          inputId={id}
          value={selected}
          options={options}
          onChange={(option) => onChange(option ? option.value : "")}
          isClearable
          isLoading={!ready}
          isDisabled={disabled}
          isOptionDisabled={(option) => option.taken === true}
          formatOptionLabel={(option, meta) => {
            if (option.unknown) {
              return (
                <span>
                  {option.label} <em className="warranty-help-inline">({NOT_IN_LIST_TEXT})</em>
                </span>
              );
            }
            if (option.taken && meta.context === "menu") {
              return (
                <span>
                  {option.label} <em className="warranty-help-inline">({TAKEN_TEXT})</em>
                </span>
              );
            }
            return option.label;
          }}
          className="godown-select-container"
          classNamePrefix="godown-select"
          styles={selectStyles}
          placeholder={ready ? "Choose a product group" : "Loading product groups"}
          noOptionsMessage={() => "No product group matches"}
          menuPlacement="auto"
          menuPosition="fixed"
          aria-invalid={hasError}
          aria-errormessage={hasMessages ? `${id}-msg` : undefined}
        />
      </div>
      <Messages id={`${id}-msg`} messages={messages} />
    </Form.Group>
  );
};

export default ProductGroupField;
